/**
 * Settings → Appearance (Aurora A4).
 *
 * Per-user, per-app accent colour. One row per scope: the platform default
 * (used on the app picker + settings) plus one per installed app. Each row is
 * a native colour picker + hex field + live `deriveAccent` preview (button /
 * active-pill / chip) + "reset to default".
 *
 * Source of truth is `GET/PATCH /api/platform/preferences`; changing a colour
 * PATCHes that scope and — because `useAppAccent` reads the same query via
 * `useAccentPrefs` — the target app repaints immediately. Unset scopes fall
 * back to Indigo (`PLATFORM_DEFAULT_ACCENT`).
 */

import { useEffect, useState } from 'react';
import {
  deriveAccent,
  onAccentText,
  PLATFORM_DEFAULT_ACCENT,
  type AccentTheme,
  type Density,
  type Theme,
} from '@k-os/ui';
import { APP_SLUGS, PLATFORM_APPS } from '@k-os/core';
import {
  usePreferences,
  usePatchPreference,
  type PreferenceScope,
  type UserPreferenceDto,
} from '../../api/preferences';
import { useTweaksContext } from '../../lib/tweaks-context';
import { Segmented } from '../../components/Segmented';
import styles from './AppearanceSettings.module.css';

const THEME_OPTIONS: { value: Theme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

const DENSITY_OPTIONS: { value: Density; label: string }[] = [
  { value: 'compact', label: 'Compact' },
  { value: 'regular', label: 'Regular' },
  { value: 'comfy', label: 'Comfy' },
];

interface ScopeRowConfig {
  scope: PreferenceScope;
  label: string;
  hint: string;
}

const ROWS: ScopeRowConfig[] = [
  {
    scope: 'platform',
    label: 'Platform default',
    hint: 'App picker, settings, and anywhere outside a specific app.',
  },
  ...APP_SLUGS.map((slug) => ({
    scope: slug as PreferenceScope,
    label: PLATFORM_APPS[slug].name,
    hint: PLATFORM_APPS[slug].description,
  })),
];

/** Track the live `<html data-theme>` so previews match light / dark. */
function useHtmlTheme(): AccentTheme {
  const [theme, setTheme] = useState<AccentTheme>(() =>
    typeof document !== 'undefined' && document.documentElement.dataset.theme === 'dark'
      ? 'dark'
      : 'light',
  );
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const read = () => setTheme(root.dataset.theme === 'dark' ? 'dark' : 'light');
    read();
    const observer = new MutationObserver(read);
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);
  return theme;
}

/** `#RRGGBB` (upper-cased) if `raw` is a valid 6-digit hex, else null. */
function normalizeHex(raw: string): string | null {
  const h = raw.trim().replace(/^#/, '');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return `#${h.toUpperCase()}`;
}

export function AppearanceSettingsPage() {
  const query = usePreferences();
  const patch = usePatchPreference();
  const theme = useHtmlTheme();
  const { tweaks, setTweak } = useTweaksContext();

  const byScope = new Map<string, UserPreferenceDto>();
  for (const p of query.data?.preferences ?? []) byScope.set(p.scope, p);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Appearance</h1>
        <p className={styles.sub}>
          Set the theme and density for the whole platform, then pick an accent
          colour for each app. Unset apps use the Indigo default.
        </p>
      </header>

      <div className={styles.rows}>
        <div className={styles.row}>
          <div className={styles.rowInfo}>
            <div className={styles.rowLabel}>Theme</div>
            <div className={styles.rowHint}>Light or dark surfaces across every app.</div>
          </div>
          <div className={styles.controls}>
            <Segmented
              ariaLabel="Theme"
              value={tweaks.theme}
              options={THEME_OPTIONS}
              onChange={(v) => setTweak('theme', v)}
            />
          </div>
        </div>

        <div className={styles.row}>
          <div className={styles.rowInfo}>
            <div className={styles.rowLabel}>Density</div>
            <div className={styles.rowHint}>
              How tightly rows and controls are packed.
            </div>
          </div>
          <div className={styles.controls}>
            <Segmented
              ariaLabel="Density"
              value={tweaks.density}
              options={DENSITY_OPTIONS}
              onChange={(v) => setTweak('density', v)}
            />
          </div>
        </div>

        {ROWS.map((row) => (
          <AccentRow
            key={row.scope}
            config={row}
            pref={byScope.get(row.scope)}
            theme={theme}
            onPatch={(accentHex) => patch.mutate({ scope: row.scope, accentHex })}
            saving={patch.isPending && patch.variables?.scope === row.scope}
          />
        ))}
      </div>
    </div>
  );
}

interface AccentRowProps {
  config: ScopeRowConfig;
  pref: UserPreferenceDto | undefined;
  theme: AccentTheme;
  onPatch: (accentHex: string | null) => void;
  saving: boolean;
}

function AccentRow({ config, pref, theme, onPatch, saving }: AccentRowProps) {
  const stored = pref?.accentHex ?? null;
  const base = stored ?? PLATFORM_DEFAULT_ACCENT;

  // Local hex-field buffer so partial / invalid typing doesn't PATCH until it
  // resolves to a valid colour. Re-sync when the stored value changes elsewhere.
  const [draft, setDraft] = useState(base);
  useEffect(() => setDraft(base), [base]);

  const shades = deriveAccent(base, theme);
  const onAccent = onAccentText(shades.a);

  const commitDraft = () => {
    const hex = normalizeHex(draft);
    if (hex && hex !== base.toUpperCase()) onPatch(hex);
    else setDraft(base); // revert invalid / unchanged input to the source value
  };

  return (
    <div className={styles.row}>
      <div className={styles.rowInfo}>
        <div className={styles.rowLabel}>{config.label}</div>
        <div className={styles.rowHint}>{config.hint}</div>
      </div>

      <div className={styles.controls}>
        <label className={styles.swatch} style={{ background: base }}>
          <span className={styles.srOnly}>{`${config.label} colour`}</span>
          <input
            type="color"
            className={styles.colorInput}
            value={base}
            onChange={(e) => onPatch(normalizeHex(e.target.value) ?? PLATFORM_DEFAULT_ACCENT)}
            aria-label={`${config.label} colour picker`}
          />
        </label>

        <input
          type="text"
          className={styles.hexField}
          value={draft}
          spellCheck={false}
          maxLength={7}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          aria-label={`${config.label} hex value`}
        />

        <button
          type="button"
          className={styles.reset}
          disabled={stored === null || saving}
          onClick={() => onPatch(null)}
        >
          Reset to default
        </button>
      </div>

      <div className={styles.preview} aria-hidden="true">
        <span
          className={styles.pvButton}
          style={{ background: shades.a, color: onAccent }}
        >
          Button
        </span>
        <span
          className={styles.pvPill}
          style={{ background: shades.soft, color: shades.ink }}
        >
          Active
        </span>
        <span
          className={styles.pvChip}
          style={{ borderColor: shades.a, color: shades.ink }}
        >
          Chip
        </span>
      </div>
    </div>
  );
}
